import { useMemo, useState } from 'react';
import { AssignPlaylistButton } from 'academy-common-lib/features/playlistAssignments/components/AssignPlaylistButton';
import { ContentAssignButton } from 'academy-common-lib/features/contentAssignments/components/ContentAssignButton';
import { UnAssignPlaylistButton } from 'academy-common-lib/features/playlistAssignments/components/UnAssignPlaylistButton';
import PlaylistAssignmentItemProgress from './PlaylistAssignmentItemProgress';
import PlaylistAssignmentProgress from './PlaylistAssignmentProgress';
import { useFilteredPlaylistAssignmentsForPortal } from '../playlistAssignmentsHooks';
import { useFilteredContentAssignmentsForPortal } from '../contentAssignmentsHooks';
import { useGetPlaylistProgressForUsersQuery } from '../../../../__generated__/chirp/com/hubspot/academy/user/progress/rpc/UserProgressReaderChirpService.hooks';
import { TABLE_PAGINATOR_SIZE } from '../../table';
import { useFullPlaylist } from '../../savedItems/savedItemsQueries.migrated';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { HR } from 'foundations-components/transitional/components/HR';
import { View } from 'foundations-components/transitional/components/View';
import { ExpandableSection } from 'foundations-components/transitional/components/ExpandableSection';
import { Text } from 'foundations-components/components/Text';
import { Box } from 'foundations-components/transitional/components/Box';
import { List } from 'foundations-components/transitional/components/List';
import { Flex } from 'foundations-components/components/Flex';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PlaylistAssignmentsForPortalContent = ({
  assignment: {
    playlistId,
    portalId,
    assignee: {
      userId
    },
    dueDate,
    id
  },
  userProgress,
  contentType,
  contentName
}) => {
  const {
    displayedPlaylistItems
  } = useFullPlaylist(playlistId);
  const {
    cardModels
  } = useLearningContentCardModels({
    items: (displayedPlaylistItems === null || displayedPlaylistItems === void 0 ? void 0 : displayedPlaylistItems.map(item => {
      const itemProgress = userProgress === null || userProgress === void 0 ? void 0 : userProgress.itemsProgress.find(ip => ip.itemId === item.itemId);
      const mappedProgress = itemProgress ? {
        // Only fields used by UI; remaining provided for compatibility
        status: readUnhandledEnumCase(itemProgress.status),
        itemId: item.itemId,
        progressLevel: itemProgress.progressLevel,
        type: item.type,
        language: item.language || undefined,
        updatedAt: itemProgress.updatedAt
      } : null;
      return {
        id: item.itemId,
        type: item.type,
        language: item.language,
        playlistProgress: mappedProgress
      };
    })) || [],
    skip: !(displayedPlaylistItems !== null && displayedPlaylistItems !== void 0 && displayedPlaylistItems.length),
    skipProgress: true
  });
  if (!cardModels) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      children: [/*#__PURE__*/_jsx(Box, {
        grow: 1,
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "learningCenter.playlistAssignments.dueDate",
          options: {
            date: contentType ? /*#__PURE__*/_jsx(ContentAssignButton, {
              assignmentIds: [id],
              contentId: playlistId,
              contentName: contentName || '',
              contentType: contentType,
              portalId: portalId,
              responsive: false,
              use: "link",
              children: dueDate ? /*#__PURE__*/_jsx(FormattedDateTime, {
                format: "L",
                type: "utc",
                value: dueDate
              }) : /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.common.add"
              })
            }) : /*#__PURE__*/_jsx(AssignPlaylistButton, {
              assignmentIds: [id],
              playlistId: playlistId,
              portalId: portalId,
              responsive: false,
              use: "link",
              children: dueDate ? /*#__PURE__*/_jsx(FormattedDateTime, {
                format: "L",
                type: "utc",
                value: dueDate
              }) : /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.common.add"
              })
            })
          }
        })
      }), /*#__PURE__*/_jsx(Box, {
        paddingInlineEnd: "200",
        children: /*#__PURE__*/_jsx(UnAssignPlaylistButton, {
          assignments: [id],
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.playlistAssignments.removeUser"
          })
        })
      })]
    }), /*#__PURE__*/_jsx(HR, {
      distance: "small"
    }), /*#__PURE__*/_jsx(List, {
      childStyles: {
        marginBlockEnd: '400'
      },
      lastChildStyles: {
        marginBlockEnd: '0'
      },
      marginBlockStart: "500",
      wrapChildren: true,
      children: cardModels.map(item => {
        return /*#__PURE__*/_jsx(PlaylistAssignmentItemProgress, {
          item: item,
          playlistId: playlistId
        }, `${playlistId}_${portalId}_${userId}_${item.id}`);
      })
    })]
  });
};
const PlaylistAssignmentsForPortalRow = ({
  assignment,
  userProgress,
  contentType,
  contentName
}) => {
  const ExpandableTitle = ({
    titleText
  }) => {
    return /*#__PURE__*/_jsx(View, {
      backgroundColor: "fill-surface-default",
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(Text, {
        as: "h2",
        typography: "heading-50",
        children: titleText
      })
    });
  };
  return /*#__PURE__*/_jsx(View, {
    backgroundColor: "fill-surface-default",
    children: /*#__PURE__*/_jsx(ExpandableSection, {
      Title: ExpandableTitle,
      "data-test-id": `row_${assignment.assignee.userId}`,
      inlineSize: "100%",
      titleText: /*#__PURE__*/_jsx(PlaylistAssignmentProgress, {
        assignment: assignment,
        userProgress: userProgress
      }),
      children: /*#__PURE__*/_jsx(PlaylistAssignmentsForPortalContent, {
        assignment: assignment,
        contentName: contentName,
        contentType: contentType,
        userProgress: userProgress
      })
    })
  });
};
const PlaylistAssignmentsForPortalList = ({
  playlistId,
  portalId,
  contentType,
  contentName
}) => {
  var _contentResult$conten;
  const [page, setPage] = useState(1);
  const useContentFlow = !!contentType;
  const playlistResult = useFilteredPlaylistAssignmentsForPortal({
    playlistId,
    portalId,
    page,
    skip: useContentFlow
  });
  const contentResult = useFilteredContentAssignmentsForPortal({
    contentId: playlistId,
    contentType: contentType || 'PLAYLIST',
    portalId,
    page,
    skip: !useContentFlow
  });
  const playlistAssignmentsForPortal = useContentFlow ? (_contentResult$conten = contentResult.contentAssignmentsForPortal) === null || _contentResult$conten === void 0 ? void 0 : _contentResult$conten.map(a => Object.assign({}, a, {
    playlistId: a.contentId
  })) : playlistResult.playlistAssignmentsForPortal;
  const pageCount = useContentFlow ? contentResult.pageCount : playlistResult.pageCount;
  const userIds = useMemo(() => {
    if (!playlistAssignmentsForPortal) return [];
    const ids = playlistAssignmentsForPortal.map(assignment => assignment.assignee.userId);
    return Array.from(new Set(ids));
  }, [playlistAssignmentsForPortal]);
  const {
    data: usersProgressCollection
  } = useGetPlaylistProgressForUsersQuery({
    variables: {
      playlistId,
      userIds
    },
    skip: userIds.length === 0
  });
  const userProgressByUserId = useMemo(() => {
    var _usersProgressCollect;
    const map = new Map();
    usersProgressCollection === null || usersProgressCollection === void 0 || (_usersProgressCollect = usersProgressCollection.results) === null || _usersProgressCollect === void 0 || _usersProgressCollect.forEach(p => map.set(p.userId, p));
    return map;
  }, [usersProgressCollection]);
  if (!playlistAssignmentsForPortal) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      as: "section",
      children: playlistAssignmentsForPortal.map(assignment => /*#__PURE__*/_jsx(PlaylistAssignmentsForPortalRow, {
        assignment: assignment,
        contentName: contentName,
        contentType: contentType,
        userProgress: userProgressByUserId.get(assignment.assignee.userId)
      }, `${playlistId}_${portalId}_${assignment.assignee.userId}`))
    }), pageCount > 1 && /*#__PURE__*/_jsx(View, {
      marginBlockStart: "300",
      children: /*#__PURE__*/_jsx(Paginator, {
        maxVisiblePageButtons: TABLE_PAGINATOR_SIZE,
        onPageChange: ({
          target: {
            value
          }
        }) => setPage(value),
        page: page,
        pageCount: pageCount
      })
    })]
  });
};
export default PlaylistAssignmentsForPortalList;